import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import apiClient from '../api/client'
import { useAuth } from '../auth/useAuth'
import { comoSeDibuja } from '../burbujas'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'
import PageHeader from '../components/PageHeader'

interface Message {
  message_id: string
  sender: 'user' | 'agent' | 'operator' | 'precal_cliente' | 'precal_bot'
  text: string
  timestamp: string
}

interface Alert {
  team: string
  text: string
  node: string
  status: string
  created_at: string
}

interface ConversationDetailData {
  phone: string
  contact_name: string | null
  status: string
  messages: Message[]
  // Resultado del árbol de decisión documentario
  basic_data?: Record<string, string>
  checklist?: string[]
  case_outcome?: string
  checklist_generated_at?: string
  tree_answers?: Record<string, string>
  alerts?: Alert[]
}

// ── Etiquetas ─────────────────────────────────────────────────────────────────

const DATA_LABELS: Record<string, string> = {
  nombre_completo:     'Nombre completo',
  documento_identidad: 'Documento',
  celular:             'Celular',
  email:               'Correo',
  direccion_inmueble:  'Dirección del inmueble',
}

// Orden del documento — evita que el objeto se pinte en orden arbitrario
const DATA_ORDER = [
  'nombre_completo', 'documento_identidad', 'celular', 'email', 'direccion_inmueble',
]

const OUTCOME_LABELS: Record<string, string> = {
  NO_PROCEDE:      'No procede',
  STAND_BY:        'En stand by',
  REVISION_HUMANA: 'Requiere revisión',
}

const OUTCOME_STYLES: Record<string, { bg: string; color: string; border: string }> = {
  NO_PROCEDE:      { bg: 'var(--c-danger-light)',  color: 'var(--c-danger-ink)',  border: 'var(--c-danger-border)' },
  STAND_BY:        { bg: 'var(--c-warn-light)',  color: 'var(--c-warn)',  border: 'var(--c-warn-border)' },
  REVISION_HUMANA: { bg: 'var(--c-info-light)', color: 'var(--c-info-ink)',  border: 'var(--c-info-border)' },
}

const TEAM_STYLES: Record<string, { bg: string; color: string }> = {
  TLMK:       { bg: 'var(--c-info-light)', color: 'var(--c-info-ink)' },
  COMERCIAL:  { bg: 'var(--c-brand-light)', color: 'var(--c-brand-hover)' },
  LEGAL:      { bg: 'var(--c-danger-light)',  color: 'var(--c-danger-ink)' },
  TASACIONES: { bg: 'var(--c-purple-light)', color: 'var(--c-purple-ink)' },
}

const DEFAULT_TEAM_STYLE = { bg: 'var(--c-surface-faint)', color: 'var(--c-ink-muted)' }

// Documentos base: los recoge y clasifica el flujo automático de subida.
// El resto del checklist lo revisa TLMK manualmente.
const BASE_DOCS = [
  'DNI del solicitante',
  'Copia Literal completa y actualizada del inmueble',
  'HR - Hoja Resumen',
  'PU - Predio Urbano',
  'Recibo de luz o agua',
]

function fmtDateTime(ts?: string) {
  if (!ts) return '—'
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('es-PE', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

// ── Panel del expediente ──────────────────────────────────────────────────────

function SectionTitle({ children, count }: { children: React.ReactNode; count?: number }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '8px', fontSize: '10.5px', fontWeight: 700,
      letterSpacing: '0.06em', color: 'var(--c-ink-muted)', textTransform: 'uppercase',
    }}>
      {children}
      {count !== undefined && (
        <span style={{
          background: 'var(--c-surface-faint)', color: 'var(--c-ink-muted)',
          fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '20px',
        }}>
          {count}
        </span>
      )}
    </div>
  )
}

function OutcomeBanner({ outcome }: { outcome: string }) {
  const st = OUTCOME_STYLES[outcome] ?? DEFAULT_TEAM_STYLE
  const border = 'border' in st ? st.border : 'transparent'
  return (
    <div style={{
      background: st.bg, border: `1px solid ${border}`, borderRadius: '10px',
      padding: '10px 12px', fontSize: '12.5px', fontWeight: 700, color: st.color,
    }}>
      {OUTCOME_LABELS[outcome] ?? outcome}
    </div>
  )
}

function CaseFilePanel({ data }: { data: ConversationDetailData }) {
  const basic = data.basic_data ?? {}
  const checklist = data.checklist ?? []
  // `data.alerts ?? []` devolvía un array NUEVO en cada render, así que el
  // useMemo de abajo se recalculaba siempre y no cacheaba nada.
  const alerts = useMemo(() => data.alerts ?? [], [data.alerts])
  const hasBasic = Object.values(basic).some(Boolean)

  const alertsByTeam = useMemo(() => {
    const grouped: Record<string, Alert[]> = {}
    for (const a of alerts) {
      const team = a.team || 'OTROS'
      ;(grouped[team] ??= []).push(a)
    }
    return grouped
  }, [alerts])

  if (!hasBasic && checklist.length === 0 && alerts.length === 0) {
    return (
      <div style={{
        background: 'white', border: '1px solid var(--c-surface-border)', borderRadius: '16px',
        padding: '20px', fontSize: '12.5px', color: 'var(--c-ink-muted)', textAlign: 'center',
      }}>
        El cliente aún no ha completado el cuestionario de evaluación.
        <div style={{ fontSize: '11.5px', marginTop: '6px', color: 'var(--c-ink-faint)' }}>
          Aquí aparecerán sus datos, el checklist documentario y las alertas internas.
        </div>
      </div>
    )
  }

  return (
    <div style={{
      background: 'white', border: '1px solid var(--c-surface-border)', borderRadius: '16px',
      padding: '18px', display: 'flex', flexDirection: 'column', gap: '18px',
      maxHeight: '100%', overflowY: 'auto',
    }} className="scrollbar-thin">

      {data.case_outcome && <OutcomeBanner outcome={data.case_outcome} />}

      {/* Datos básicos */}
      {hasBasic && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <SectionTitle>Datos declarados</SectionTitle>
          <div style={{ fontSize: '12.5px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
            {DATA_ORDER.filter((k) => basic[k]).map((k) => (
              <div key={k} style={{ display: 'flex', gap: '8px' }}>
                <span style={{ color: 'var(--c-ink-muted)', minWidth: '130px', flexShrink: 0 }}>
                  {DATA_LABELS[k] ?? k}
                </span>
                <span style={{ fontWeight: 600, wordBreak: 'break-word' }}>{basic[k]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Checklist documentario */}
      {checklist.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <SectionTitle count={checklist.length}>Checklist documentario</SectionTitle>
          <ul style={{ display: 'flex', flexDirection: 'column', gap: '4px', margin: 0, padding: 0, listStyle: 'none' }}>
            {checklist.map((doc) => {
              const esBase = BASE_DOCS.includes(doc)
              return (
                <li key={doc} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12.5px',
                  padding: '5px 8px', borderRadius: '8px',
                  background: esBase ? 'var(--c-surface-subtle)' : 'transparent',
                }}>
                  <span style={{ color: 'var(--c-ink-faint)', flexShrink: 0 }}>▢</span>
                  <span style={{ flex: 1, wordBreak: 'break-word' }}>{doc}</span>
                  {esBase && (
                    <span style={{
                      fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.03em', flexShrink: 0,
                      color: 'var(--c-ink-muted)', background: 'var(--c-surface-border)',
                      padding: '2px 6px', borderRadius: '20px', marginTop: '1px',
                    }}>
                      BASE
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
          <p style={{ fontSize: '11px', color: 'var(--c-ink-faint)', lineHeight: 1.5 }}>
            Los documentos marcados <b>BASE</b> se piden y clasifican automáticamente.
            El resto los revisa TLMK al recibirlos.
          </p>
          {data.checklist_generated_at && (
            <p style={{ fontSize: '11px', color: 'var(--c-ink-faint)' }}>
              Generado el {fmtDateTime(data.checklist_generated_at)}
            </p>
          )}
        </div>
      )}

      {/* Alertas internas */}
      {alerts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <SectionTitle count={alerts.length}>Alertas internas</SectionTitle>
          {Object.entries(alertsByTeam).map(([team, items]) => {
            const st = TEAM_STYLES[team] ?? DEFAULT_TEAM_STYLE
            return (
              <div key={team} style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <span style={{
                  alignSelf: 'flex-start', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em',
                  background: st.bg, color: st.color, padding: '2px 8px', borderRadius: '20px',
                }}>
                  {team}
                </span>
                {items.map((a, i) => (
                  <div key={`${a.node}-${i}`} style={{
                    fontSize: '12px', padding: '8px 10px', borderRadius: '8px',
                    background: 'var(--c-surface-subtle)', borderLeft: `3px solid ${st.color}`,
                  }}>
                    <div style={{ lineHeight: 1.45 }}>{a.text}</div>
                    <div style={{
                      fontSize: '10.5px', color: 'var(--c-ink-faint)', marginTop: '3px',
                      fontFamily: "'IBM Plex Mono', monospace",
                    }}>
                      {a.node}
                    </div>
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Página ────────────────────────────────────────────────────────────────────

export default function ConversationDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { canOperate } = useAuth()
  // El contenedor de los mensajes: se baja moviendo SU scroll (DEF-031).
  const mensajesRef = useRef<HTMLDivElement>(null)

  const [data, setData] = useState<ConversationDetailData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [replyText, setReplyText] = useState('')
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const [showCaseFile, setShowCaseFile] = useState(true)

  const canReply = canOperate

  const fetchConversation = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient.get<ConversationDetailData>(`/conversations/${id}`)
      setData(response.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar la conversación')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { void fetchConversation() }, [fetchConversation])
  // Sin `scrollIntoView` (DEF-031, 07-oct-2026): ése mueve también la página,
  // y en horizontal, y dejaba el menú lateral fuera de la vista.
  useEffect(() => {
    const el = mensajesRef.current
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }, [data?.messages])

  const handleReply = async () => {
    if (!replyText.trim() || !id) return
    setSending(true)
    setSendError(null)
    try {
      await apiClient.post(`/conversations/reply`, {
        conversation_id: id,
        text: replyText.trim(),
      })
      setReplyText('')
      await fetchConversation()
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Error al enviar el mensaje')
    } finally {
      setSending(false)
    }
  }

  if (loading) return <LoadingSpinner label="Cargando conversación..." />
  if (error)   return <ErrorAlert message={error} onRetry={fetchConversation} />

  const displayName = data?.contact_name ?? data?.phone ?? id ?? ''
  const subtitle = data?.contact_name
    ? `${data.phone} · Estado: ${data?.status ?? ''}`
    : data?.status ? `Estado: ${data.status}` : undefined

  const alertCount = data?.alerts?.length ?? 0
  const checklistCount = data?.checklist?.length ?? 0
  const hasCaseFile = alertCount > 0 || checklistCount > 0
    || Object.values(data?.basic_data ?? {}).some(Boolean)

  return (
    <div className="flex h-full flex-col gap-4">
      <PageHeader
        detailTitle={`Conversación — ${displayName}`}
        detailSubtitle={subtitle}
        breadcrumbs={[
          { label: 'Conversaciones', href: '/conversations' },
          { label: displayName },
        ]}
      />

      {/* Toggle del expediente */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <button
          onClick={() => setShowCaseFile((v) => !v)}
          style={{
            all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px',
            padding: '6px 13px', borderRadius: '9px', fontSize: '12px', fontWeight: 600,
            background: showCaseFile ? 'var(--c-info-light)' : 'var(--c-surface-faint)',
            color: showCaseFile ? 'var(--c-info-ink)' : 'var(--c-ink-muted)',
          }}
        >
          📋 Expediente
          {hasCaseFile && (
            <span style={{
              background: showCaseFile ? 'var(--c-info-ink)' : 'var(--c-ink-faint)',
              color: 'white', fontSize: '10px', fontWeight: 700,
              padding: '1px 6px', borderRadius: '20px',
            }}>
              {checklistCount}
            </span>
          )}
        </button>
        {alertCount > 0 && (
          <span style={{
            fontSize: '11.5px', fontWeight: 600, color: 'var(--c-danger-ink)',
            background: 'var(--c-danger-light)', padding: '5px 11px', borderRadius: '20px',
          }}>
            ⚠ {alertCount} {alertCount === 1 ? 'alerta interna' : 'alertas internas'}
          </span>
        )}
      </div>

      {/* Mensajes + expediente */}
      <div style={{
        flex: 1, minHeight: 0, display: 'grid', gap: '16px',
        gridTemplateColumns: showCaseFile ? 'minmax(0, 1fr) minmax(0, 380px)' : '1fr',
      }}>
        <div ref={mensajesRef} className="card overflow-y-auto scrollbar-thin p-4" style={{ minHeight: 0 }}>
          {!data?.messages || data.messages.length === 0 ? (
            <p className="text-center text-sm text-ink-muted">No hay mensajes en esta conversación.</p>
          ) : (
            <div className="space-y-3">
              {/* Sin las anotaciones del bot del lado del cliente (DEF-079). */}
              {data.messages.filter((msg) => comoSeDibuja(msg) !== 'nada').map((msg) => {
                // `precal_cliente`: lo que el cliente le escribió al precalificador del
                // socio antes del pase. Es del cliente, va de su lado.
                const isUser = msg.sender === 'user' || msg.sender === 'precal_cliente'
                return (
                  <div key={msg.message_id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[70%] rounded-2xl px-4 py-2 text-sm ${
                        isUser
                          ? 'rounded-br-none bg-brand text-white'
                          : 'rounded-bl-none bg-surface-subtle text-ink'
                      }`}
                    >
                      <p style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</p>
                      <p className={`mt-1 text-xs ${isUser ? 'text-white/60' : 'text-ink-faint'}`}>
                        {new Date(msg.timestamp).toLocaleTimeString('es-PE', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                        {msg.sender === 'operator' && <span className="ml-1">(operador)</span>}
                        {msg.sender === 'precal_bot' && <span className="ml-1">(precalificador del socio)</span>}
                        {msg.sender === 'precal_cliente' && <span className="ml-1">(al precalificador)</span>}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {showCaseFile && data && (
          <div style={{ minHeight: 0, display: 'flex' }}>
            <CaseFilePanel data={data} />
          </div>
        )}
      </div>

      {/* Reply box */}
      {canReply ? (
        <div className="space-y-2">
          {sendError && <ErrorAlert message={sendError} />}
          <div className="flex gap-2">
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void handleReply()
              }}
              placeholder="Escribe tu respuesta… (Ctrl+Enter para enviar)"
              rows={3}
              className="input flex-1 resize-none"
            />
            <button
              onClick={() => void handleReply()}
              disabled={sending || !replyText.trim()}
              className="btn-primary self-end disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sending ? 'Enviando…' : 'Responder'}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-center text-xs text-ink-muted">
          Solo Operadores y Administradores pueden responder conversaciones.
        </p>
      )}

      <button
        onClick={() => navigate('/conversations')}
        className="self-start text-sm text-ink-muted hover:text-ink hover:underline"
      >
        ← Volver a conversaciones
      </button>
    </div>
  )
}
